import React, { useMemo } from "react";
import {
  abortBetIntent,
  activeIntentsSignal,
  betAgentSignal,
} from "./betAgent";
import { AgentEvent, AgentState, BLOCKING_STATES } from "./types";
import { useSignalValue } from "../../../../utils/signal";
import { currentUserHasBetAgentAccess } from "../../../login/hasAccess";
import styles from "./AgentStatus.module.scss";

const STATE_LABELS: Record<AgentState, string> = {
  queued: "Queued",
  opening_tab: "Opening sportsbook…",
  tab_ready: "Page loaded",
  checking_auth: "Checking sign-in…",
  needs_login: "Log in to sportsbook first",
  prompting_creds: "Credentials not available in MVP",
  logging_in: "Signing in…",
  awaiting_manual_auth: "Finish login in sportsbook",
  finding_market: "Finding market…",
  market_found: "Market found",
  adding_to_slip: "Adding to slip…",
  slip_verified: "Slip verified",
  confirming_stake: "Filling stake…",
  stake_set: "Stake filled",
  submitting: "Submitting bet…",
  done_navigated: "Page opened",
  done_prefilled: "Slip ready — review in sportsbook",
  done_placed: "Bet placed",
  aborted: "Cancelled",
  error: "Error",
};

const TERMINAL_OK: ReadonlySet<AgentState> = new Set<AgentState>([
  "done_navigated",
  "done_prefilled",
  "done_placed",
]);

/**
 * Sticky bottom-right drawer that shows live state for every active
 * agent intent. Mounted once at the App root. Disappears when there
 * are no active intents.
 */
const AgentStatusDrawer: React.FC = () => {
  const ext = useSignalValue(betAgentSignal);
  const intents = useSignalValue(activeIntentsSignal);

  const entries = useMemo(
    () => Array.from(intents.entries()).map(([id, evt]) => ({ id, evt })),
    [intents]
  );

  // Admin-only gate. Mirrors the SendToAgentButton check; non-admins
  // never see the drawer even if they somehow have the extension
  // installed and an intent broadcast in the wild.
  if (!currentUserHasBetAgentAccess()) return null;
  if (!ext.installed) return null;
  if (entries.length === 0) return null;

  return (
    <div className={styles.drawer} role="status" aria-live="polite">
      {entries.map(({ id, evt }) => (
        <AgentStatusRow key={id} event={evt} />
      ))}
    </div>
  );
};

interface RowProps {
  event: AgentEvent;
}

const AgentStatusRow: React.FC<RowProps> = ({ event }) => {
  const isError = event.state === "error";
  const isOk = TERMINAL_OK.has(event.state);
  const isBlocking = BLOCKING_STATES.has(event.state);

  const tone = isError
    ? styles.drawerRowError
    : isOk
    ? styles.drawerRowOk
    : isBlocking
    ? styles.drawerRowBlocking
    : "";

  return (
    <div className={`${styles.drawerRow} ${tone}`}>
      <div className={styles.drawerRowLeft}>
        <SpinnerOrIcon state={event.state} />
        <div className={styles.drawerRowText}>
          <div className={styles.drawerRowTitle}>
            {STATE_LABELS[event.state] ?? event.state}
          </div>
          {(event.message || event.reason) && (
            <div className={styles.drawerRowSub}>
              {event.reason ?? event.message}
            </div>
          )}
          {event.ticketId && (
            <div className={styles.drawerRowSub}>
              Ticket {event.ticketId}
            </div>
          )}
        </div>
      </div>
      {!isOk && !isError && event.state !== "aborted" && (
        <button
          type="button"
          className={styles.drawerCancel}
          onClick={() => abortBetIntent(event.intentId)}
          title="Cancel"
        >
          Cancel
        </button>
      )}
    </div>
  );
};

const SpinnerOrIcon: React.FC<{ state: AgentState }> = ({ state }) => {
  if (state === "error") {
    return <span className={styles.drawerIconError}>!</span>;
  }
  if (TERMINAL_OK.has(state)) {
    return <span className={styles.drawerIconOk}>✓</span>;
  }
  if (state === "aborted") {
    return <span className={styles.drawerIconError}>×</span>;
  }
  return <span className={styles.drawerSpinner} />;
};

export default AgentStatusDrawer;
